import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileSystemItem } from "types";
import Breadcrumb, {
  BreadcrumbItem,
} from "components/file-system/file-breadcrumb";
import { FolderStats } from "components/file-system/FolderStats";
import { FolderViewController } from "components/file-system/FolderViewController";
import { FolderListView } from "components/file-system/folder-view/FolderListView";
import { FolderGridView } from "components/file-system/folder-view/FolderGridView";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import Loader from "components/Loader";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { Library } from "constants/library";
import { useTranslation } from "react-i18next";

interface FileSelectViewProps {
  selectedFiles: FileSystemItem[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileSystemItem[]>>;
  filterFile?: (items: FileSystemItem[]) => FileSystemItem[];
  rootFolderName?: string;
  selectionMode?: "file" | "folder";
}

const FileSelectView: React.FC<FileSelectViewProps> = ({
  selectedFiles = [],
  setSelectedFiles,
  filterFile,
  rootFolderName = Library.DOCUMENTS,
  selectionMode = "file",
}) => {
  const { t } = useTranslation();
  const [currentItemId, setCurrentItemId] = useState<string>("/");
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState<"name" | "size">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [files, setFiles] = useState<FileSystemItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const isWorkflow = rootFolderName === Library.WORKFLOWS;
      const endpoint = isWorkflow
        ? `/folders/${currentItemId}/workflows`
        : `/folders/${currentItemId}/documents`;

      if (currentItemId && currentItemId !== "/") {
        const resp = await api.get(endpoint);
        let items = resp.data;
        if (isWorkflow) {
          // map workflow to FileSystemItem
          items = items.map((item: any) => ({
            ...item,
            type: "Workflow",
            document_metadata: {
              file_size: 0,
            },
          }));
        }
        const filtered = filterFile ? filterFile(items) : items;
        setFiles(filtered);
      }
      if (currentItemId === "/") {
        const resp = await api.get("/folders");
        const rootItems = resp.data.map((item: FileSystemItem) => ({
          ...item,
          type: item.type || "Folder",
        }));
        const itemId =
          rootItems.find((i: any) => i.name === rootFolderName)?.id || "/";

        const ep = isWorkflow
          ? `/folders/${itemId}/workflows`
          : `/folders/${itemId}/documents`;

        const respp = await api.get(ep);
        let items = respp.data;
        if (isWorkflow) {
          // map workflow to FileSystemItem
          items = items.map((item: any) => ({
            ...item,
            type: "Workflow",
            document_metadata: {
              file_size: 0,
            },
          }));
        }
        setFiles(items);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "CanceledError") {
        return;
      }
      handleAxiosAndCaughtError(error);
      setError("Failed to fetch items.");
    } finally {
      setLoading(false);
    }
  }, [currentItemId, filterFile, rootFolderName]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const filteredItems = useMemo(() => {
    return files.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, files]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems].sort((a, b) => {
      let compareValue = 0;
      if (sortBy === "name") {
        compareValue = a.name.localeCompare(b.name);
      } else if (sortBy === "size") {
        // If either is a folder, treat as 0 for size
        const aSize =
          a.type === "Document"
            ? Number(a.document_metadata.file_size) || 0
            : 0;
        const bSize =
          b.type === "Document"
            ? Number(b.document_metadata.file_size) || 0
            : 0;
        compareValue = aSize - bSize;
      }
      return sortOrder === "asc" ? compareValue : -compareValue;
    });
    // Folders first, then files
    return sorted.sort((a, b) => {
      if (a.type === "Folder" && b.type === "Document") return -1;
      if (a.type === "Document" && b.type === "Folder") return 1;
      return 0;
    });
  }, [filteredItems, sortBy, sortOrder]);

  const handleSort = (by: "name" | "size") => {
    if (sortBy === by) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(by);
      setSortOrder("asc");
    }
  };

  // Check if a document is selectable based on its status
  const isDocumentSelectable = (item: FileSystemItem) => {
    if (item.type !== "Document") return true;
    const status = item.status || item?.document_metadata?.status;
    // Handle both string values and enum values
    const statusString = status as string;
    return (
      statusString !== "Processing" &&
      statusString !== "Failed" &&
      statusString !== "PROCESSING" &&
      statusString !== "FAILED"
    );
  };

  // When navigating into a folder, append to breadcrumb
  const handleItemClick = (item: FileSystemItem) => {
    if (item.type === "Folder") {
      setCurrentItemId(item.id);
      setBreadcrumb([...breadcrumb, { name: item.name, id: item.id }]);
    } else {
      if (item.type === "Document" && selectionMode === "folder") return;

      // Prevent selection of documents with Processing or Failed status
      if (!isDocumentSelectable(item)) return;

      setSelectedFiles((prev) => {
        if (prev.some((file) => file.id === item.id)) {
          return prev.filter((file) => file.id !== item.id);
        }
        return [...prev, item];
      });
    }
  };

  // When clicking a breadcrumb, truncate and set currentItemId
  const handleBreadcrumbClick = (id: string, index: number) => {
    if (id === "/" || index === -1) {
      setCurrentItemId("/");
      setBreadcrumb([]);
    } else {
      setCurrentItemId(id);
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    }
  };

  return (
    <div className="mx-auto min-h-[60vh] max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Breadcrumb
          breadcrumb={breadcrumb}
          onPathClick={handleBreadcrumbClick}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="w-full">
            <SearchAndNew onChange={setQuery} value={query} showAdd={false} />
          </div>
          <FolderViewController
            onSort={handleSort}
            onViewModeChange={setViewMode}
            sortOrder={sortOrder}
            sortBy={sortBy}
            viewMode={viewMode}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader txt="Getting items..." />
        </div>
      ) : error || !files ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12">
          <p className="text-lg font-medium text-red-500">{error}</p>
          <button
            onClick={fetchFiles}
            className="rounded-lg bg-blue-500 px-4 py-1 text-white hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* File/Folder List */}
          <div className=" max-h-[30vh] overflow-auto rounded-lg border   shadow-sm lg:max-h-[50vh]">
            {viewMode === "list" ? (
              <FolderListView
                fileItems={sortedItems}
                onSort={handleSort}
                onFileClick={handleItemClick}
                setSelectedFiles={setSelectedFiles}
                willUpdateUrl={false}
                selectedFiles={selectedFiles}
                willShowCheckbox={isDocumentSelectable}
              />
            ) : (
              <FolderGridView
                fileItems={sortedItems}
                onFileClick={handleItemClick}
                setSelectedFiles={setSelectedFiles}
                willUpdateUrl={false}
                selectedFiles={selectedFiles}
                willShowCheckbox={isDocumentSelectable}
              />
            )}
          </div>

          {/* Stats */}
          <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
            <FolderStats fileItems={sortedItems} />

            {selectedFiles.length > 0 && (
              <div className="font-medium text-blue-600">
                {filterFile
                  ? t("library.filesSelectedIncludingCurrent", {
                      count: selectedFiles.length,
                    })
                  : t("library.filesSelected", { count: selectedFiles.length })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FileSelectView;
